import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Banner } from "@altek/ui/components/feedback/Banner";
import { Modal } from "@altek/ui/components/feedback/Modal";
import { useAuth } from "@/app/providers/AuthProvider";
import { candidateDetail, impersonationCandidates } from "@/features/admin/lib/impersonation-candidates";
import { Combobox } from "@altek/ui/components/form/Combobox";
import { SelectField } from "@altek/ui/components/form/Select";
import { TextareaField } from "@altek/ui/components/form/TextField";
import { Button } from "@altek/ui/components/ui/Button";
import { adminApi } from "@/features/admin/api/admin.api";
import { impersonationCandidatesQuery } from "@/features/admin/api/admin.queries";
import { messageFor } from "@altek/ui/lib/problem";
import { supportCodeOf } from "@/lib/api/support-code";

/**
 * The server's floor WHEN a reason is given. It is currently optional — see the note on
 * `impersonateSchema`; an empty box is accepted and the row records that none was asked for.
 * A half-typed one is not: five characters is a slip, not a justification.
 */
const REASON_MIN = 10;

/**
 * SUNUCUNUN TAVANI (`impersonationCandidatesQuery`: `max(25)`), eskiden 10 istiyorduk.
 *
 * 10 İKEN HESAPLAR SESSİZCE DÜŞÜYORDU — 24 Ekim 2026, kullanıcı: «yönetici patron pazarlama
 * hesaplarını niye kaldırdın». Hiçbiri kaldırılmamıştı: aday sayısı 13'e çıkınca sorgu ilk 10'u
 * alıyordu ve sıralama `primaryEmail` ARTAN olduğu için düşenler hep alfabenin sonu oluyordu —
 * `patron`, `pazarlama`, `satis`, `yonetici`. Yani kırpma rastgele değil, aynı hesapları
 * sistematik olarak gizliyordu, ve liste bunu hiçbir yerde söylemiyordu.
 *
 * SINIRI BÜSBÜTÜN KALDIRMAK ÇÖZÜM DEĞİL: bin kişilik bir kurulumda bütün hesapları tek yanıtta
 * çekmek listeyi okunmaz, yanıtı ağır yapardı — ve sunucu zaten 25'in üstünü kabul etmiyor.
 * Çözüm sessizliği kaldırmak: tavana DEĞİLSE hepsi görünüyor, tavana DEĞDİYSE liste bunu
 * söylüyor ve aramayı daraltmayı istiyor (`hint` aşağıda).
 */
const CANDIDATE_LIMIT = 25;

export const IMPERSONATION_WINDOW_KEY = "altek.impersonation.window";


/**
 * "Kullanıcı değiştir" — sign in AS somebody, to see exactly what they see.
 *
 * NOT a preview built from their permissions: the server issues a real token for the target,
 * and every request after it resolves against THEIR grants, THEIR scope and THEIR record
 * policy. A preview assembled on the client would agree with the real thing right up until it
 * did not, and the screen that disagreed would be the one somebody filed a bug about.
 *
 * What the window is not allowed to do is stated on the form rather than discovered: a support
 * session is refused every permission marked dangerous, so nobody opens one expecting to fix a
 * role assignment through it.
 *
 * …UNLESS THE INSTALLATION IS IN DEVELOPMENT MODE (`session.supportSessionUnrestricted`, server
 * `SUPPORT_SESSION_UNRESTRICTED`, 30 October 2026): then the window is the person themself and
 * the form says so instead — the restriction list would describe refusals that never come.
 */
export function ImpersonateDialog({
  open,
  onOpenChange,
  onStarted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStarted: () => Promise<void>;
}) {
  const { session } = useAuth();
  const [targetUserId, setTargetUserId] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [minutes, setMinutes] = useState("30");
  const [term, setTerm] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) return;
    setTargetUserId(null);
    setReason("");
    setMinutes("30");
    setTerm("");
    setTouched(false);
  }, [open]);

  /**
   * ADAYLAR AD, BİRİM VE ROLLERLE (21 Ekim 2026, kullanıcı: «hesaplar yazarken hangi departmanda olduğu da yazsın ve
   * rolleri de yazsın; sistem yöneticisi olduğumuz için bu menüde bir daha sistem yöneticisi görmemize gerek yok»).
   * Liste e-posta dökümüydü; hangi hesabın «filo», hangisinin «depo» olduğu ancak adresten tahmin ediliyordu. Sunucu
   * kendinizi ve yönetim rolü taşıyan hesapları düşüyor.
   */
  const users = useQuery({
    ...impersonationCandidatesQuery({ ...(term ? { q: term } : {}), limit: CANDIDATE_LIMIT }),
    enabled: open,
  });

  /**
   * Tavana değdiyse liste KESİLMİŞ OLABİLİR ve bunu söylemek zorunda.
   *
   * Sunucu toplam sayı döndürmüyor, dönen tek bilgi satır sayısı; tam tavana eşitse «bu kadarmış»
   * ile «gerisi kesildi» ayırt edilemiyor. O belirsizlik kullanıcıya AÇIKÇA söyleniyor — sessizce
   * ilk 25'i gösterip gerisini yok saymak, bu düzeltmenin düzelttiği hatanın ta kendisi.
   */
  const mayBeTruncated = (users.data?.length ?? 0) >= CANDIDATE_LIMIT;

  const start = useMutation({
    mutationFn: () =>
      adminApi.impersonation.start({
        targetUserId: targetUserId ?? "",
        // Omitted rather than sent empty: the server records "none was asked for", which is
        // a truthful audit row. A blank string would be a reason that says nothing.
        ...(written ? { reason: written } : {}),
        minutes: Number(minutes),
      }),
    onSuccess: async (started) => {
      // Remembered so the window can be CLOSED on the way back rather than left to expire.
      // Session storage, not local: it belongs to this tab's support window and nothing else.
      try {
        sessionStorage.setItem(IMPERSONATION_WINDOW_KEY, started.impersonationSessionId);
      } catch {
        /* the window still closes itself when it expires */
      }
      onOpenChange(false);
      await onStarted();
    },
  });

  const written = reason.trim();
  const reasonTooShort = written.length > 0 && written.length < REASON_MIN;
  const canSubmit = targetUserId !== null && !reasonTooShort && !start.isPending;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      width={560}
      title="Kullanıcı değiştir"
      description="Seçtiğiniz kişinin hesabına geçersiniz: ekranları, yetkileri ve kapsamı onunkidir. Her adım denetim kaydına yazılır ve o kişinin geçmişinde de görünür."
      isPending={start.isPending}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={start.isPending}
          >
            Vazgeç
          </Button>
          <Button
            loading={start.isPending}
            disabled={!canSubmit}
            {...(canSubmit
              ? {}
              : {
                  disabledReason:
                    targetUserId === null
                      ? "Önce bir kullanıcı seçin."
                      : `Gerekçe ya boş bırakılır ya da en az ${REASON_MIN} karakter olur.`,
                })}
            onClick={() => start.mutate()}
          >
            Bu kullanıcıya geç
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {start.error ? (
          <Banner
            tone="danger"
            title="Geçiş yapılamadı"
            requestId={supportCodeOf(start.error)}
          >
            {messageFor(start.error)}
          </Banner>
        ) : null}

        {session?.supportSessionUnrestricted ? (
          <Banner tone="info" title="Geliştirme kipi">
            Geçtiğiniz kişinin yapabildiği her şeyi yapabilir, görebildiği her şeyi görebilirsiniz;
            her adım denetime yazılır.
          </Banner>
        ) : (
          <Banner tone="info" title="Bu oturumda yapamayacaklarınız">
            Kritik risk taşıyan işlemler — rol yönetimi ve atama, şirket ve modül yapılandırması,
            hassas veri okuma, bakiye düzeltme, belge iptali — destek oturumunda reddedilir.
          </Banner>
        )}

        <Combobox
          label="Kullanıcı"
          required
          value={targetUserId}
          onChange={setTargetUserId}
          /**
           * YOURSELF IS FILTERED OUT, and the hint below already promised it.
           *
           * The list showed the signed-in account like any other, so the promise and the list
           * disagreed — you could pick yourself, and the server refused with an error that read
           * like a fault. A control that offers a choice it will then refuse is worse than one
           * that never offered it: the person learns the product is unreliable rather than that
           * the action is meaningless.
           */
          options={impersonationCandidates(users.data ?? [], session?.user.id).map((user) => {
            const detail = candidateDetail(user);
            return {
              value: user.id,
              label: user.fullName ? `${user.fullName} · ${user.primaryEmail}` : user.primaryEmail,
              // Spread rather than `?? undefined`: `ComboboxOption.description` is an optional
              // string, and `exactOptionalPropertyTypes` rejects an explicit undefined.
              ...(detail ? { description: detail } : {}),
            };
          })}
          onSearch={setTerm}
          isLoading={users.isFetching}
          placeholder="Ad ya da e-posta ile arayın"
          hint={
            mayBeTruncated
              ? `Yalnızca aktif hesaplar; kendi hesabınız ve sistem yöneticisi hesapları listede yok. İlk ${CANDIDATE_LIMIT} hesap gösteriliyor — aradığınız kişi yoksa adını ya da e-postasını yazın.`
              : "Yalnızca aktif hesaplar; kendi hesabınız ve sistem yöneticisi hesapları listede yok."
          }
        />

        <TextareaField
          label="Gerekçe"
          rows={2}
          maxLength={500}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          onBlur={() => setTouched(true)}
          hint="İsteğe bağlı. Yazarsanız denetim kaydına ve o kişinin geçmişine geçer; boş bırakırsanız kayıt gerekçe istenmediğini söyler."
          error={
            touched && reasonTooShort
              ? `Ya boş bırakın ya da en az ${REASON_MIN} karakter yazın.`
              : null
          }
        />

        <SelectField
          label="Süre"
          value={minutes}
          onChange={(value) => setMinutes(value ?? "30")}
          options={[
            { value: "15", label: "15 dakika" },
            { value: "30", label: "30 dakika" },
            { value: "60", label: "1 saat" },
            { value: "120", label: "2 saat" },
          ]}
          hint="Süre dolunca oturum kendiliğinden kapanır."
        />
      </div>
    </Modal>
  );
}
